import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  MessageCircle, Plus, ExternalLink, Clock, 
  CheckCircle, AlertCircle, Coins, Send, ArrowLeft,
  ChevronDown, ChevronUp, Shield
} from 'lucide-react';

interface SutraData {
  sutra_id: string;
  sutra_number: number;
  initial_message: string;
  summary: string | null;
  paid_until: string;
  status: string;
  whose_turn: string;
  created_at: string;
  file_path?: string;
  username?: string;
}

interface PendingSutra {
  sutra_id: string;
  sutra_number: number;
  initial_message: string;
  paid_until: string;
  created_at: string;
  user_name: string;
  user_email: string;
}

interface SutraResponse {
  success: boolean;
  data: {
    sutras: SutraData[];
    total: number;
    username: string;
    profile_url: string | null;
  };
  message?: string;
}

const SUTRA_COST = 5.5;
const MAX_WORDS = 30;
const MIN_WORDS = 5;
const ADMIN_EMAIL = 'ahantravyuh@gmail.com';

const Sutra: React.FC = () => {
  const { token, user } = useAuth();
  const [sutras, setSutras] = useState<SutraData[]>([]);
  const [pendingSutras, setPendingSutras] = useState<PendingSutra[]>([]);
  const [username, setUsername] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Reply states
  const [expandedSutra, setExpandedSutra] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);
  
  // Admin states
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminSummary, setAdminSummary] = useState('');
  const [responding, setResponding] = useState<string | null>(null);

  const API_URL = process.env.REACT_APP_API_URL || '/api';
  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    fetchSutras();
    if (isAdmin) {
      fetchPendingSutras();
    }
  }, []);

  const fetchSutras = async () => {
    try {
      const response = await fetch(`${API_URL}/sutra/my-sutras`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data: SutraResponse = await response.json();
      
      if (data.success) {
        setSutras(data.data.sutras);
        setUsername(data.data.username);
      }
    } catch (err) {
      console.error('Error fetching sutras:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingSutras = async () => {
    try {
      const response = await fetch(`${API_URL}/sutra/admin/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setPendingSutras(data.data.pending);
      }
    } catch (err) {
      console.error('Error fetching pending sutras:', err);
    }
  };

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const wordCount = countWords(newMessage);
  const replyWordCount = countWords(replyMessage);
  const isValidLength = wordCount >= MIN_WORDS && wordCount <= MAX_WORDS;
  const isValidReplyLength = replyWordCount >= 1 && replyWordCount <= MAX_WORDS;
  const hasEnoughAhant = (user?.ahant_balance || 0) >= SUTRA_COST;

  const handleCreateSutra = async () => {
    if (!isValidLength || !hasEnoughAhant) return;

    setCreating(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/sutra/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ message: newMessage })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Sutra created! You spent ${SUTRA_COST} AHANT.`);
        setNewMessage('');
        setShowCreateForm(false);
        fetchSutras();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.message || 'Failed to create sutra');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleReply = async (sutraId: string) => {
    if (!isValidReplyLength) return;

    setReplying(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/sutra/${sutraId}/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ message: replyMessage })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Reply sent! Waiting for admin response.');
        setReplyMessage('');
        setExpandedSutra(null);
        fetchSutras();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.message || 'Failed to send reply');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setReplying(false);
    }
  };

  const handleAdminRespond = async (sutraId: string) => {
    setResponding(sutraId);
    setError('');

    try {
      const response = await fetch(`${API_URL}/sutra/admin/respond/${sutraId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ summary: adminSummary || undefined })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Marked as responded. User can now reply.');
        setAdminSummary('');
        fetchPendingSutras();
        fetchSutras();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.message || 'Failed to respond');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setResponding(null);
    }
  };

  const getStatusBadge = (sutra: SutraData) => {
    const isActive = sutra.status === 'active' && new Date(sutra.paid_until) > new Date();
    
    if (!isActive) {
      return (
        <span className="flex items-center text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 mr-1" />
          Archived
        </span>
      );
    }

    if (sutra.whose_turn === 'admin') {
      return (
        <span className="flex items-center text-yellow-400 text-sm">
          <Clock className="w-4 h-4 mr-1" />
          Waiting for response
        </span>
      );
    }

    return (
      <span className="flex items-center text-green-400 text-sm">
        <CheckCircle className="w-4 h-4 mr-1" />
        Your turn to reply
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getUsernameFromName = (name: string) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Loading Sutras...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 pb-24">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <a href="/" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-6 h-6" />
            </a>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center">
                <MessageCircle className="w-8 h-8 mr-3 text-purple-400" />
                My Sutras
              </h1>
              <p className="text-gray-400 mt-1">Collaborative strings with admin</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isAdmin && (
              <button
                onClick={() => setShowAdminPanel(!showAdminPanel)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all font-medium ${
                  showAdminPanel 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-amber-500/20 border border-amber-500/50 text-amber-300 hover:bg-amber-500/30'
                }`}
              >
                <Shield className="w-5 h-5" />
                <span>Admin ({pendingSutras.length})</span>
              </button>
            )}
            {!showCreateForm && !showAdminPanel && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-lg transition-all font-medium"
              >
                <Plus className="w-5 h-5" />
                <span>New Sutra</span>
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-500/20 border border-green-500/50 text-green-200 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* Admin Panel */}
        {isAdmin && showAdminPanel && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6">
            <h2 className="text-xl font-bold text-amber-300 mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              Admin Panel - Pending Responses ({pendingSutras.length})
            </h2>
            
            {pendingSutras.length === 0 ? (
              <p className="text-gray-400">No sutras waiting for response.</p>
            ) : (
              <div className="space-y-4">
                {pendingSutras.map((sutra) => (
                  <div key={sutra.sutra_id} className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-white font-medium">{sutra.user_name}</p>
                        <p className="text-gray-400 text-sm">{sutra.user_email}</p>
                      </div>
                      <span className="text-gray-400 text-sm">Sutra #{sutra.sutra_number}</span>
                    </div>
                    
                    <p className="text-gray-300 mb-3 bg-white/5 p-3 rounded-lg">
                      "{sutra.initial_message}"
                    </p>
                    
                    <div className="flex items-center space-x-2 mb-3">
                      <a
                        href={`/sutra/${getUsernameFromName(sutra.user_name)}-${sutra.sutra_number}.html`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-400 hover:text-purple-300 text-sm flex items-center"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Edit HTML
                      </a>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        placeholder="Summary (optional)"
                        value={adminSummary}
                        onChange={(e) => setAdminSummary(e.target.value)}
                        className="flex-1 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm"
                      />
                      <button
                        onClick={() => handleAdminRespond(sutra.sutra_id)}
                        disabled={responding === sutra.sutra_id}
                        className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                      >
                        {responding === sutra.sutra_id ? 'Saving...' : 'Mark Responded'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create Form */}
        {showCreateForm && !showAdminPanel && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4">Start a New Sutra</h2>
            
            <div className="space-y-4">
              {/* Cost Info */}
              <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <div className="flex items-center text-amber-300">
                  <Coins className="w-5 h-5 mr-2" />
                  <span>Cost: {SUTRA_COST} AHANT/month</span>
                </div>
                <div className={hasEnoughAhant ? 'text-green-400' : 'text-red-400'}>
                  Balance: {Number(user?.ahant_balance || 0).toFixed(2)} AHANT
                </div>
              </div>

              {/* Message Input */}
              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  Your message ({MIN_WORDS}-{MAX_WORDS} words)
                </label>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="What would you like to collaborate on? Be specific about your goals..."
                  className="w-full h-32 px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
                />
                
                {/* Word Counter */}
                <div className="flex justify-between mt-2">
                  <span className={`text-sm ${
                    wordCount < MIN_WORDS ? 'text-gray-400' : 
                    wordCount > MAX_WORDS ? 'text-red-400' : 'text-green-400'
                  }`}>
                    {wordCount} / {MAX_WORDS} words
                    {wordCount < MIN_WORDS && ` (minimum ${MIN_WORDS})`}
                  </span>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewMessage('');
                    setError('');
                  }}
                  className="flex-1 py-3 bg-gray-600/50 hover:bg-gray-600/70 text-white rounded-lg transition-all font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSutra}
                  disabled={!isValidLength || !hasEnoughAhant || creating}
                  className={`flex-1 py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-all ${
                    isValidLength && hasEnoughAhant && !creating
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white'
                      : 'bg-gray-600/30 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Send className="w-5 h-5" />
                  <span>{creating ? 'Creating...' : `Pay ${SUTRA_COST} AHANT & Create`}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sutras List */}
        {!showAdminPanel && (
          <>
            {sutras.length === 0 && !showCreateForm ? (
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-12 border border-white/20 text-center">
                <MessageCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No Sutras Yet</h3>
                <p className="text-gray-400 mb-6">
                  Start a collaboration thread with admin. Each sutra costs {SUTRA_COST} AHANT per month.
                </p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-lg transition-all font-medium"
                >
                  Create Your First Sutra
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {sutras.map((sutra) => {
                  const isExpanded = expandedSutra === sutra.sutra_id;
                  const canReply = sutra.status === 'active' && 
                                   sutra.whose_turn === 'user' && 
                                   new Date(sutra.paid_until) > new Date();

                  return (
                    <div
                      key={sutra.sutra_id}
                      className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 hover:border-purple-500/50 transition-all overflow-hidden"
                    >
                      <div className="p-5">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="text-lg font-semibold text-white">
                              Sutra #{sutra.sutra_number}
                            </h3>
                            <p className="text-sm text-gray-400">
                              Created {formatDate(sutra.created_at)} • Active until {formatDate(sutra.paid_until)}
                            </p>
                          </div>
                          {getStatusBadge(sutra)}
                        </div>
                        
                        <p className="text-gray-300 mb-4 line-clamp-2">
                          {sutra.summary || sutra.initial_message}
                        </p>

                        <div className="flex items-center justify-between">
                          <a
                            href={sutra.file_path || `/sutra/${username}-${sutra.sutra_number}.html`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors text-sm font-medium"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            View Full Conversation
                          </a>

                          {canReply && (
                            <button
                              onClick={() => {
                                setExpandedSutra(isExpanded ? null : sutra.sutra_id);
                                setReplyMessage('');
                              }}
                              className="flex items-center text-green-400 hover:text-green-300 text-sm font-medium"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronUp className="w-4 h-4 mr-1" />
                                  Close
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-4 h-4 mr-1" />
                                  Reply
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Reply Form */}
                      {isExpanded && canReply && (
                        <div className="border-t border-white/10 p-5 bg-white/5">
                          <label className="block text-sm text-gray-300 mb-2">
                            Your reply (max {MAX_WORDS} words)
                          </label>
                          <textarea
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            placeholder="Type your reply..."
                            className="w-full h-24 px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none resize-none"
                          />
                          
                          <div className="flex justify-between items-center mt-3">
                            <span className={`text-sm ${
                              replyWordCount > MAX_WORDS ? 'text-red-400' : 'text-gray-400'
                            }`}>
                              {replyWordCount} / {MAX_WORDS} words
                            </span>
                            
                            <button
                              onClick={() => handleReply(sutra.sutra_id)}
                              disabled={!isValidReplyLength || replying}
                              className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-all ${
                                isValidReplyLength && !replying
                                  ? 'bg-green-500 hover:bg-green-600 text-white'
                                  : 'bg-gray-600/30 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              <Send className="w-4 h-4" />
                              <span>{replying ? 'Sending...' : 'Send Reply'}</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Info Box */}
        {!showAdminPanel && (
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <h4 className="text-purple-300 font-medium mb-2">How Sutras Work</h4>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>• Pay {SUTRA_COST} AHANT to start a 30-day collaboration thread</li>
              <li>• Send messages up to {MAX_WORDS} words each</li>
              <li>• Admin responds to each message</li>
              <li>• Renew monthly to keep the conversation active</li>
            </ul>
          </div>
        )}

      </div>
    </div>
  );
};

export default Sutra;
